import React, { Component, ErrorInfo, ReactNode } from 'react';

import { useLocation, Location } from 'react-router';
import * as Sentry from '@sentry/react';

import NotFound from 'routes/NotFound';
import { isStaleBuildError, reloadForStaleBuild } from 'utils/staleBuild';

// Long enough for the report to reach Sentry on a normal connection, short
// enough that a user whose report cannot be delivered is not left waiting.
const SENTRY_FLUSH_TIMEOUT_MS = 2_000;

interface Props {
  children: ReactNode;
  location: Location;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {

  // Log the error to CloudWatch RUM
      const payload = {
        errorMessage: error.message,
        stack: error.stack,
        errorInfo,
        pathname: window.location.pathname,
        timestamp: new Date().toISOString(),
      };
      if ((window as any ).AWSRum?.recordEvent) {
        (window as any).AWSRum.recordEvent('errorBoundary', payload);
      }

    // Report the error to Sentry with the React component stack attached.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });

    // Continue logging to the console
    console.error('Uncaught error:', error, errorInfo);

    // A chunk this session asked for is no longer on the CDN, because a deploy
    // renamed it. The session is running code that has been deleted, not code
    // that is broken, so reload into the build that is actually there rather
    // than telling the user through <NotFound /> that a page which exists does
    // not. When the reload is refused — already reloaded once, or
    // sessionStorage unreadable — the <NotFound /> fallback below stands, which
    // is the intended floor: an error screen beats a refresh loop (PLAT-6541).
    //
    // The reload waits for the Sentry report to be sent. captureException only
    // queues it, and a reload can cancel the request in flight: Sentry's
    // transport relies on fetch keepalive, and its own source lists Firefox as
    // not supporting that. The rate of this error is how we know old builds
    // have drained, so losing it would hide exactly what we need to see.
    if (isStaleBuildError(error)) {
      Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS).finally(reloadForStaleBuild);
    }
  }


  // Listen for changes to the location prop. When the route changes, reset the error state.
  componentDidUpdate(prevProps: Props) {
    if (prevProps.location !== this.props.location && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      // Render your NotFound fallback UI when an error is caught.
      return <NotFound />;
    }
    return this.props.children;
  }
}

// Create a wrapper that injects the current location into your ErrorBoundary.
const ErrorBoundaryWithLocation = (props: { children: ReactNode }) => {
  const location = useLocation();
  return <ErrorBoundary location={location}>{props.children}</ErrorBoundary>;
};

export { ErrorBoundary };
export default ErrorBoundaryWithLocation;
